import { getStatusLabel } from '../../../status-labels';
import type { AccountLevel, SalesOrderStatus } from '../../../types';
import type { BillStatus } from '../../../types/bills';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';
import {
  isOrderChargeBillLocked,
  ORDER_CHARGE_BILL_LOCKED_MESSAGE,
  orderChargeDeliveredPriceBillLock,
} from './orderChargeBillLock';

export type SalesOrderExpenseAccountLevel = `${AccountLevel}`;

export type SalesOrderExpenseFacts = {
  salesOrderStatus: SalesOrderStatus;
  billStatuses: readonly BillStatus[];
  salesOrderHasOpenInvoice: boolean;
  accountLevel: SalesOrderExpenseAccountLevel | null;
  businessEntityMatchesCustomer: boolean;
  isDraft: boolean;
  /**
   * The charge itself has a bill line, in any bill status — the api's delivered-price gate
   * (`orderChargeDeliveredPriceBillLock`). undefined = not known, treated as none: the api asks
   * that gate before the owner lock, for every host, and the grid's fact builder does not read it yet.
   */
  chargeHasBillLine?: boolean;
};

export const SALES_ORDER_EXPENSE_FIELDS = [
  'expenseType',
  'accountLevel',
  'ledgerAccount',
  'businessEntity',
  'chargePer',
  'quantity',
  'unitAmount',
  'includedInDlvPrice',
  'description',
  'linkedLineItems',
] as const;

export type SalesOrderExpenseField = (typeof SALES_ORDER_EXPENSE_FIELDS)[number];

export const SALES_ORDER_EXPENSE_OPS = ['clear', 'duplicate', 'repair incomplete allocation'] as const;
export type SalesOrderExpenseOp = (typeof SALES_ORDER_EXPENSE_OPS)[number];

const EDITABLE_ORDER_STATUSES: readonly SalesOrderStatus[] = [
  'DRAFT',
  'PENDING',
  'SUBMITTED',
  'APPROVED',
  'PICKING',
  'SHIPPED',
  'CLOSED',
];

const actionNotApplicable = (): Decision =>
  deny('SALES_ORDER_EXPENSE_ACTION_NOT_APPLICABLE', 'Not available for this charge.');

const decideDeliveredPrice = (facts: SalesOrderExpenseFacts): Decision => {
  const billLine = orderChargeDeliveredPriceBillLock(facts.chargeHasBillLine === true);
  if (!billLine.allowed) return billLine;

  if (facts.salesOrderHasOpenInvoice) {
    return deny('SALES_ORDER_EXPENSE_DLV_INVOICE_OPEN', 'Delivered pricing is locked once the invoice is open.');
  }

  if (
    facts.accountLevel === 'ACCOUNTS_PAYABLE' ||
    (facts.accountLevel === 'ACCOUNTS_RECEIVABLE' && facts.businessEntityMatchesCustomer)
  ) {
    return allow();
  }

  return deny('SALES_ORDER_EXPENSE_DLV_NOT_APPLICABLE', "Delivered pricing doesn't apply to this charge.");
};

const fieldRule = (
  facts: SalesOrderExpenseFacts,
  action: Action<SalesOrderExpenseField, SalesOrderExpenseOp>
): Decision =>
  action.type === 'edit' && action.field === 'includedInDlvPrice' ? decideDeliveredPrice(facts) : allow();

const fieldRules = {
  expenseType: fieldRule,
  accountLevel: fieldRule,
  ledgerAccount: fieldRule,
  businessEntity: fieldRule,
  chargePer: fieldRule,
  quantity: fieldRule,
  unitAmount: fieldRule,
  includedInDlvPrice: fieldRule,
  description: fieldRule,
  linkedLineItems: fieldRule,
} satisfies Record<SalesOrderExpenseField, typeof fieldRule>;

export const salesOrderExpensePolicy: Policy<SalesOrderExpenseFacts, SalesOrderExpenseField, SalesOrderExpenseOp> =
  definePolicy<SalesOrderExpenseFacts, SalesOrderExpenseField, SalesOrderExpenseOp>({
    kind: 'sales order charge',
    fields: SALES_ORDER_EXPENSE_FIELDS,
    ops: SALES_ORDER_EXPENSE_OPS,
    requires: {},
    entity: (facts, action) => {
      if (action.type === 'view') return allow();
      if (action.type === 'perform' && action.op === 'repair incomplete allocation') return allow();

      if (!EDITABLE_ORDER_STATUSES.includes(facts.salesOrderStatus)) {
        return deny(
          'SALES_ORDER_CHARGES_LOCKED',
          `Charges are locked once the sales order is ${getStatusLabel(facts.salesOrderStatus)}.`
        );
      }

      const changesDeliveredPrice = action.type === 'edit' && action.field === 'includedInDlvPrice';
      if (!changesDeliveredPrice && isOrderChargeBillLocked(facts.billStatuses)) {
        return deny('SALES_ORDER_EXPENSE_BILL_LOCKED', ORDER_CHARGE_BILL_LOCKED_MESSAGE);
      }

      if (action.type === 'delete') return facts.isDraft ? actionNotApplicable() : allow();
      if (action.type === 'perform' && action.op === 'clear') {
        return facts.isDraft ? allow() : actionNotApplicable();
      }
      if (action.type === 'perform' && action.op === 'duplicate') {
        return facts.isDraft ? actionNotApplicable() : allow();
      }

      return allow();
    },
    field: fieldRules,
  });
